import React, { useEffect, useRef, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import AppointmentForm, { AppointmentFormProps } from "../appointment-form";

/**
 * The «Κλείστε ραντεβού» popup (D47), rebuilt on the native <dialog> element.
 *
 * react-modal is gone. <dialog> gives the things it was emulating for free and
 * more correctly: focus trapping, Escape to close, inert background, and a
 * ::backdrop that needs no portal or `setAppElement` call. It also removes a
 * dependency that had not kept pace with React 19.
 *
 * This is an ENHANCEMENT, not the only door. The same form renders as an
 * ordinary page at /kleiste-rantevou, which is what makes it usable as a
 * Google Ads sitelink, shareable, and functional without JavaScript.
 */
export interface AppointmentModalProps {
  showModal: boolean;
  setShowModal: (flag: boolean) => void;
  heading?: string;
  prompt?: string;
  closeLabel?: string;
  /** which θέση opened the dialog (hero, header, sidebar…) — becomes the
   *  submit label `modal:<source>` so the funnel is per-θέση end to end */
  source?: string;
}

export default function AppointmentModal({
  showModal,
  setShowModal,
  heading,
  prompt,
  closeLabel,
  source,
}: AppointmentModalProps) {
  const { t } = useTranslation("main");
  const ref = useRef<HTMLDialogElement>(null);
  // the form (and its Turnstile iframe — ~90KiB of third-party) mounts on
  // FIRST open, not on page load: the closed dialog was costing every page
  // a Turnstile challenge nobody could see. Stays mounted after that so the
  // solved token survives close/reopen.
  const [everOpened, setEverOpened] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (showModal) setEverOpened(true);
    if (showModal && !dialog.open) dialog.showModal();
    if (!showModal && dialog.open) dialog.close();
  }, [showModal]);

  const close = () => setShowModal(false);

  const appointmentFormProps: AppointmentFormProps = {
    appointmentHeading: heading ?? t("booking.defaultHeading"),
    appointmentPrompt: prompt ?? t("booking.defaultPrompt"),
    view: "modal",
    placement: `modal:${source ?? "unknown"}`,
    closeBookAppointmentModal: close,
  };

  return (
    <dialog
      ref={ref}
      // fires on Escape as well as close() — one handler covers both
      onClose={close}
      // clicking the backdrop closes; clicks inside the panel do not bubble here
      onClick={(e) => { if (e.target === ref.current) close(); }}
      aria-label={t("booking.dialogAria")}
      // artifact modal-card: min(380px,100%), radius 16, deep soft shadow
      className="m-auto w-[min(23.75rem,92vw)] rounded-2xl border-0 p-0 shadow-[0_26px_60px_-20px_rgba(23,17,31,0.5)] backdrop:bg-ink/45 backdrop:backdrop-blur-[2px]"
    >
      <div className="relative bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={close}
          aria-label={closeLabel ?? t("booking.close")}
          className="absolute right-4 top-4 text-muted transition-colors hover:text-violet-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20"
               fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
        {everOpened ? <AppointmentForm {...appointmentFormProps} /> : null}
      </div>
    </dialog>
  );
}
